import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

/** 横に溢れる表はコンテナ内でスクロールさせ、ページ自体を横スクロールさせない */
export function TableWrap({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('w-full overflow-x-auto', className)} {...props} />;
}

/**
 * `min-w-` はモバイルのため。日本語は文字の間ならどこでも折り返せるので、
 * 幅が足りないと列は 1 文字ずつまで潰れる ("長い名 / 前の開 / 発メン" のように)。
 * 表として読めなくなるが、**データは 1 つも消えていない**ので気づきにくい。
 * 下限を与えて `TableWrap` の横スクロールに逃がす方が、この製品
 * (会計に効く数字を扱う) には正しい。
 *
 * 768px 以上では下限を外す。2 列に割れる画面 (アカウント・テナント設定) では
 * 器が 576px を下回るので、外さないとデスクトップの表が広がってしまう。
 *
 * **独自の `min-w-*` を渡すときは `md:` にも書くこと。** `cn` (tailwind-merge) は
 * 素の `min-w-*` と `md:min-w-*` を別物として両方残すので、素の指定だけだと
 * ここの `md:min-w-0` に 768px 以上で打ち消される (実際に収支レポートの表が
 * 1100px → 982px に縮んだ)。
 */
export function Table({ className, ...props }: ComponentProps<'table'>) {
  return (
    <table
      className={cn('w-full min-w-[36rem] border-collapse text-sm md:min-w-0', className)}
      {...props}
    />
  );
}

/** 見出し行。デザイン上、英字ラベルは等幅・字間広め。 */
export function Th({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'px-4 py-3 text-left font-mono text-[11px] font-normal tracking-wider text-ink-faint',
        className,
      )}
      {...props}
    />
  );
}

export function Td({ className, ...props }: ComponentProps<'td'>) {
  return <td className={cn('border-t border-line px-4 py-4 align-middle', className)} {...props} />;
}

/** 数値セル。工数・金額・比率は必ずこちらを使う。 */
export function TdNum({ className, ...props }: ComponentProps<'td'>) {
  return (
    <td
      className={cn(
        // 数値が単位や桁の途中で改行される ("8.5" / "h") のは常に誤りなので、
        // 呼び出し側ではなくここで止める。溢れる分は TableWrap が横スクロールにする。
        'whitespace-nowrap border-t border-line px-4 py-4 text-right align-middle font-mono text-ink-secondary',
        className,
      )}
      {...props}
    />
  );
}
